import { CreationTaskDrawer } from 'features/daily-plan';
import {
  ProfileAccountableBar,
  ProfileMenu,
  ProfilePageLayout,
  ProfileSummary,
  ProfileTabs
} from 'features/inventory-profiles/components/shared';
import {
  COMMON_TAB_IDS,
  CONTACT_DRAWERS,
  CONTACT_PROFILE_CONFIG
} from 'features/inventory-profiles/constants';
import {
  useContactProfile,
  useProfileAccountability,
  useProfileDrawer,
  useTabRefresh
} from 'features/inventory-profiles/hooks';
import { ProfileRestrictionDrawer, useProfileRestrictions } from 'features/profile-restrictions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { When } from 'UI/components/atoms';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import LinkCompanyDialog from 'UI/components/organisms/LinkCompanyDialog';
import NameEdit from 'UI/components/organisms/NameEdit';
import { EntityType, SearchProjectEntities } from 'UI/constants/entityTypes';

import { useContactNavigation, useContactPermissions } from './ContactProfileV2.hooks';
import { buildContactTabs } from './ContactProfileV2.tabs';
import {
  buildContactMenuItems,
  formatContactResponse,
  getContactAllowedTaskTypes
} from './ContactProfileV2.utils';

/**
 * Contact profile. Composes the data, permissions and actions hooks with the shared layout.
 * Rendered behind ContactProfileV2Route, which redirects converted contacts.
 * @param {Object} props
 * @param {number|string} props.id Contact (name) id
 * @param {boolean} [props.enableUrlNavigation] Sync the active tab with the URL. True by default
 *   (route); the quick view passes false
 * @param {Function} [props.onEditionCompleted] Called after a profile edit in quick-view mode
 * @param {string} [props.layoutClassName] Extra className for the content area (quick view)
 */
export const ContactProfileV2 = ({
  enableUrlNavigation = true,
  id,
  layoutClassName,
  onEditionCompleted
}) => {
  const { contact, isLoading, error, refresh, reload } = useContactProfile(id);
  const isProfileReady = !isLoading && !error && Boolean(contact.id);
  const formattedContact = formatContactResponse(contact);

  const permissions = useContactPermissions(contact);
  const restrictions = useProfileRestrictions({
    canRequestDoNotContact: permissions.canRequestDoNotContact,
    canRequestDoNotEngage: permissions.canRequestDoNotEngage,
    entityTypeId: CONTACT_PROFILE_CONFIG.entityTypeId,
    profileStatusId: contact.status?.id
  });

  const { refreshTab, getTabVersion } = useTabRefresh();
  const { drawer, openDrawer, closeDrawer, closeAndRefresh } = useProfileDrawer({
    onChanged: refresh,
    onTabChanged: refreshTab
  });
  const assignment = useProfileAccountability({
    canEdit: permissions.canEdit,
    entity: CONTACT_PROFILE_CONFIG,
    profile: contact
  });

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint: `${CONTACT_PROFILE_CONFIG.endpoint}/${id}`,
      entity: SearchProjectEntities.Contact,
      profile: { id: contact.id, name: formattedContact.header.title },
      tabKey: COMMON_TAB_IDS.SearchProjects,
      triggerTabRefresh: refreshTab,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const tabs = buildContactTabs({
    contact,
    contactName: formattedContact.header.title,
    isLoading,
    permissions,
    refresh,
    searchProjectsTab: searchProjectTabPropsDefinition
  });

  const navigation = useContactNavigation({ id });
  const menuItems = buildContactMenuItems({
    permissions,
    searchProjectMenuItems: SearchProjectsMenuItems,
    restrictionMenuItems: restrictions.menuItems,
    openDrawer,
    onAddCandidate: navigation.addCandidate
  });

  return (
    <>
      <ProfilePageLayout
        header={formattedContact.header}
        enableUrlNavigation={enableUrlNavigation}
        layoutClassName={layoutClassName}
        actions={<ProfileMenu items={menuItems} />}
        subheader={
          <>
            <ProfileSummary
              company={formattedContact.summary.company}
              location={formattedContact.summary.location}
              status={formattedContact.status}
              created={formattedContact.summary.created}
              details={formattedContact.summary.details}
            />
            <ProfileAccountableBar assignment={assignment} />
          </>
        }
        isLoading={isLoading}
        error={error}
        onRetry={reload}
      >
        <ProfileTabs
          tabs={tabs}
          getTabVersion={getTabVersion}
          enableUrlNavigation={enableUrlNavigation}
          organizationStorageKey={CONTACT_PROFILE_CONFIG.tabsStorageKey}
          organizationSubheader={CONTACT_PROFILE_CONFIG.tabsSubheader}
        />
      </ProfilePageLayout>

      <When condition={drawer === CONTACT_DRAWERS.Edit}>
        <NameEdit
          FPDrawerProps={{ open: drawer === CONTACT_DRAWERS.Edit }}
          name={contact}
          onEditCompleted={handleEntityEditionCompleted({
            handleEditCompleted: closeAndRefresh,
            enableUrlNavigation,
            onEditionCompleted
          })}
          onEditClosed={closeDrawer}
        />
      </When>
      <When condition={isProfileReady && contact.shouldAskRecruiterAddEmployer}>
        <LinkCompanyDialog
          type={EntityType.ContactType.Contact}
          entity={contact}
          onSetProfile={refresh}
        />
      </When>
      <When condition={drawer === CONTACT_DRAWERS.Task}>
        <CreationTaskDrawer
          allowedTaskTypes={getContactAllowedTaskTypes(contact.status?.id)}
          onClose={closeDrawer}
          entityData={{ id: contact.id, name: contact.personalInformation?.full_name }}
          entityType={EntityType.Contact}
          onTaskCreated={navigation.openDailyPlan}
        />
      </When>
      <When condition={restrictions.drawerProps.open}>
        <ProfileRestrictionDrawer
          entityId={id}
          entityTypeId={CONTACT_PROFILE_CONFIG.entityTypeId}
          onClose={restrictions.drawerProps.onClose}
          open={restrictions.drawerProps.open}
          selectedRestrictionType={restrictions.drawerProps.selectedRestrictionType}
        />
      </When>
      <SearchProjectForms />
    </>
  );
};
